import React, { useEffect, useMemo, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { FiEye, FiEyeOff } from "react-icons/fi";
import adminService from "../api/services/adminService";
import supportService from "../api/services/supportService";
import Loading from "../components/Loading";
import toast from "react-hot-toast";
import {
  PHONE_DIAL_CODES as phoneCodes,
  clampNationalDigitsForDial,
  getNationalLengthLimits,
  parseContactForDialSelect,
} from "../data/phoneDialCodes";
import {
  defaultsForPanelRole,
  tabLabelsForPanelRole,
} from "../constants/supportTabPermissions";

const AdminPanelUserModal = ({
  isOpen,
  onClose,
  onSuccess,
  editingUser,
  allowedRoles = null,
  defaultRole = "Admin",
  createViaSupport = false,
  modalTitle = null,
  showTabPermissions = false,
}) => {
  const style =
    "text-left tracking-wide text-grey-darker text-[0.7rem] font-[600] mb-[3px] ";
  const [loading, setLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [phoneCode, setPhoneCode] = useState("+92"); // Default to Pakistan

  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    contactNo: "",
    role: "Admin",
    agent_title: "",
  });
  const [tabPermissions, setTabPermissions] = useState(() =>
    defaultsForPanelRole(defaultRole)
  );

  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    contactNo: false,
    role: false,
  });

  useEffect(() => {
    const fetchRoles = async () => {
      if (allowedRoles?.length) {
        setRoles(allowedRoles.map((name, index) => ({ id: index + 1, name })));
        setRolesLoading(false);
        return;
      }
      try {
        setRolesLoading(true);
        const result = await adminService.getAllRoles();
        setRoles(result?.roles || []);
      } catch (error) {
        console.error("Error fetching roles:", error);
        toast.error("Failed to fetch roles");
      } finally {
        setRolesLoading(false);
      }
    };

    if (isOpen) {
      fetchRoles();
    }
  }, [isOpen, allowedRoles]);

  useEffect(() => {
    if (isOpen) {
      if (editingUser) {
        // Edit mode - populate with existing data
        const contactNo = editingUser.contactNo || "";
        const { dial, national } = parseContactForDialSelect(contactNo);
        setPhoneCode(dial);
        setData({
          firstName: editingUser.firstName || "",
          lastName: editingUser.lastName || "",
          email: editingUser.email || "",
          password: "", // Don't pre-fill password
          contactNo: national,
          role: editingUser.roleName || "Admin",
          agent_title: editingUser.agent_title || "",
        });
      } else {
        // Create mode - reset form
        setPhoneCode("+92");
        const initialRole =
          allowedRoles?.length === 1 ? allowedRoles[0] : defaultRole;
        setData({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          contactNo: "",
          role: initialRole,
          agent_title: "",
        });
        setTabPermissions(defaultsForPanelRole(initialRole));
      }
      setShowPassword(false);
      setErrors({
        firstName: false,
        lastName: false,
        email: false,
        password: false,
        contactNo: false,
        role: false,
      });
    }
  }, [isOpen, editingUser, defaultRole, allowedRoles]);

  const handleChange = (e) => {
    if (e.target.name === "contactNo") {
      const value = clampNationalDigitsForDial(phoneCode, e.target.value);
      setData((d) => ({ ...d, [e.target.name]: value }));
      if (value.length > 0) {
        setErrors((prevState) => ({ ...prevState, [e.target.name]: false }));
      }
    } else {
      const { name, value } = e.target;
      setData((d) => ({ ...d, [name]: value }));
      if (name === "role" && showTabPermissions) {
        setTabPermissions(defaultsForPanelRole(value));
      }
      if (value.length > 0) {
        setErrors((prevState) => ({ ...prevState, [name]: false }));
      }
    }
  };

  const tabLabels = useMemo(
    () => tabLabelsForPanelRole(data.role),
    [data.role]
  );

  const handlePhoneCodeChange = (e) => {
    const next = e.target.value;
    setPhoneCode(next);
    // Clamp existing digits to new max
    setData((d) => ({ ...d, contactNo: clampNationalDigitsForDial(next, d.contactNo) }));
    setErrors((prevState) => ({ ...prevState, contactNo: false }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const digits = String(phone || "").replace(/\D/g, "");
    const { min, max } = getNationalLengthLimits(phoneCode);
    return digits.length >= min && digits.length <= max;
  };

  const submit = async () => {
    let hasError = false;
    // Password is only required for new users (not when editing)
    const requiredFields = editingUser
      ? ["firstName", "lastName", "email", "contactNo", "role"]
      : ["firstName", "lastName", "email", "password", "contactNo", "role"];

    requiredFields.forEach((field) => {
      if (!data[field] || data[field].length === 0) {
        setErrors((prevState) => ({ ...prevState, [field]: true }));
        hasError = true;
      }
    });

    if (!validateEmail(data.email)) {
      setErrors((prevState) => ({ ...prevState, email: true }));
      hasError = true;
    }

    // Password validation only for new users (not when editing)
    if (!editingUser && data.password && data.password.length < 6) {
      setErrors((prevState) => ({ ...prevState, password: true }));
      hasError = true;
      toast.error("Password must be at least 6 characters long");
    }
    // For edit mode, password is optional
    if (
      editingUser &&
      data.password &&
      data.password.length > 0 &&
      data.password.length < 6
    ) {
      setErrors((prevState) => ({ ...prevState, password: true }));
      hasError = true;
      toast.error("Password must be at least 6 characters long");
    }

    if (!validatePhone(data.contactNo)) {
      setErrors((prevState) => ({ ...prevState, contactNo: true }));
      hasError = true;
      const { min, max } = getNationalLengthLimits(phoneCode);
      const range =
        min === max ? `exactly ${min} digits` : `${min}–${max} digits`;
      toast.error(`Please enter a valid phone number (${range} after the code)`);
    }

    if (hasError) {
      return;
    }

    try {
      setLoading(true);
      // Combine phone code with contact number
      const fullContactNo = `${phoneCode}${data.contactNo}`;

      if (editingUser) {
        // Update mode
        const updateData = {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          contactNo: fullContactNo,
          role: data.role,
          agent_title: data.agent_title,
        };
        // Only include password if it's provided
        if (data.password && data.password.length > 0) {
          updateData.password = data.password;
        }
        await adminService.updateAdminPanelUser(editingUser.id, updateData);
        toast.success("Admin panel user updated successfully!");
      } else {
        // Create mode
        const createData = {
          ...data,
          contactNo: fullContactNo,
        };
        if (createViaSupport) {
          await supportService.createStaffUser({
            ...createData,
            ...(showTabPermissions ? { tab_permissions: tabPermissions } : {}),
          });
          toast.success("Support user created successfully!");
        } else {
          await adminService.createAdminPanelUser(createData);
          toast.success("Admin panel user created successfully!");
        }
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(
        `Error ${editingUser ? "updating" : "creating"} admin panel user:`,
        error
      );
      const msg =
        error?.message ||
        error?.response?.data?.message ||
        `Failed to ${editingUser ? "update" : "create"} admin panel user`;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-2 md:mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-1 md:p-4 flex justify-between items-center">
          <h2 className="text-md md:text-xl font-bold text-gray-800">
            {modalTitle || (editingUser ? "Edit Admin Panel User" : "Create Admin Panel User")}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700">
            <IoMdClose className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {rolesLoading ? (
            <Loading variant="modal" />
          ) : (
            <>
              <div className="mb-4">
                <label className={style}>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={data.firstName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 text-xs md:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.firstName ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter first name"
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">
                    First name is required
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className={style}>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={data.lastName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 text-xs md:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.lastName ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter last name"
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">
                    Last name is required
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className={style}>Call title (sudo name)</label>
                <input
                  type="text"
                  name="agent_title"
                  value={data.agent_title}
                  onChange={handleChange}
                  maxLength={80}
                  className="w-full px-3 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="e.g. Ayesha"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Shown to customers as{" "}
                  <span className="font-semibold">
                    {String(data.agent_title || "").trim()
                      ? `${String(data.agent_title).trim()} ${
                          data.role === "Chat Support" ? "Naseebi Support" : "Naseebi Agent"
                        }`
                      : data.role === "Chat Support"
                        ? "Naseebi Support"
                        : "Naseebi Agent"}
                  </span>
                </p>
              </div>

              <div className="mb-4">
                <label className={style}>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={data.email}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 text-xs md:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter email"
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">
                    Valid email is required
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className={style}>Password {!editingUser && "*"}</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={data.password}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 text-xs md:text-sm pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.password ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder={
                      editingUser
                        ? "Leave blank to keep current password"
                        : "Enter password (min 6 characters)"
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none">
                    {showPassword ? (
                      <FiEyeOff size={20} />
                    ) : (
                      <FiEye size={20} />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">
                    Password must be at least 6 characters
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className={style}>Contact Number *</label>
                <div className="flex gap-2">
                  <select
                    value={phoneCode}
                    onChange={handlePhoneCodeChange}
                    className="px-1 md:px-3 py-2 text-xs md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white min-w-[120px]">
                    {phoneCodes.map((pc) => (
                      <option key={pc.code} value={pc.code}>
                        {pc.code} ({pc.country})
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    name="contactNo"
                    value={data.contactNo}
                    onChange={handleChange}
                    className={`flex-1 px-1 md:px-3 py-2 text-xs md:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.contactNo ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter phone digits"
                    maxLength={10}
                  />
                </div>
                  {errors.contactNo && (
                    <p className="text-red-500 text-xs mt-1">
                      Please enter a valid phone number for the selected country code.
                    </p>
                  )}
                {data.contactNo.length > 0 && data.contactNo.length < 10 && (
                  <p className="text-gray-500 text-xs mt-1">
                    {10 - data.contactNo.length} digits remaining
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className={style}>Role *</label>
                {allowedRoles?.length === 1 ? (
                  <div className="w-full px-3 py-2 text-xs md:text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-700">
                    {allowedRoles[0]}
                  </div>
                ) : (
                  <select
                    name="role"
                    value={data.role}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 text-xs md:text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      errors.role ? "border-red-500" : "border-gray-300"
                    }`}>
                    {roles.map((role) => (
                      <option key={role.id} value={role.name}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                )}
                {errors.role && (
                  <p className="text-red-500 text-xs mt-1">Role is required</p>
                )}
              </div>

              {showTabPermissions && createViaSupport && !editingUser && (
                <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Tab access for this user
                  </p>
                  <p className="text-[11px] text-gray-500 mb-3">
                    Choose which modules this {data.role} can see. You can change these later when editing the team.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {tabLabels.map((tab) => (
                      <label
                        key={tab.key}
                        className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={!!tabPermissions[tab.key]}
                          onChange={(e) =>
                            setTabPermissions((p) => ({
                              ...p,
                              [tab.key]: e.target.checked,
                            }))
                          }
                          className="accent-red-500"
                        />
                        {tab.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 text-xs md:text-sm justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={loading}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading
                    ? editingUser
                      ? "Updating..."
                      : "Creating..."
                    : editingUser
                    ? "Update User"
                    : "Create User"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanelUserModal;
